import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { Save as SaveIcon, OpenInNew as OpenInNewIcon, PhotoCamera as PhotoCameraIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { PublicProfileUrlField } from '@components/profile/PublicProfileUrlField';
import { publicProfilePath, suggestPublicSlug } from '@utils/publicProfileSlug';

interface AcademyProfileProps {
  academyId: string;
  academyInfo: any;
  onUpdate: () => void;
}

const AcademyProfile: React.FC<AcademyProfileProps> = ({ academyId, academyInfo, onUpdate }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  useEffect(() => {
    if (!academyId) {
      setError('Academy ID is required');
    }
  }, [academyId]);
  
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    academy_type: 'sports_academy',
    description: '',
    country: '',
    city: '',
    address: '',
    contact_email: '',
    contact_phone: '',
    public_slug: '',
  });
  const publicProfileHref = publicProfilePath(
    academyInfo?.public_slug,
    academyInfo?.academy_id_code
  );

  useEffect(() => {
    if (academyInfo) {
      setFormData({
        name: academyInfo.name || '',
        academy_type: academyInfo.academy_type || 'sports_academy',
        description: academyInfo.description || '',
        country: academyInfo.country || '',
        city: academyInfo.city || '',
        address: academyInfo.address || '',
        contact_email: academyInfo.contact_email || '',
        contact_phone: academyInfo.contact_phone || '',
        public_slug: academyInfo.public_slug || suggestPublicSlug(academyInfo.name) || '',
      });
    }
  }, [academyInfo]);

  const handleSave = async () => {
    if (!formData.name) {
      toast.error('Academy name is required');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`/api/academies/${academyId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          academy_type: formData.academy_type,
          description: formData.description || null,
          country: formData.country || null,
          city: formData.city || null,
          address: formData.address || null,
          contact_email: formData.contact_email || null,
          contact_phone: formData.contact_phone || null,
          publicSlug: formData.public_slug || undefined,
        }),
      });

      const result = await response.json();

      if (result.success) {
        toast.success('Academy profile updated successfully');
        onUpdate();
      } else {
        toast.error(result.message || 'Failed to update academy profile');
        setError(result.message || 'Failed to update academy profile');
      }
    } catch (err: any) {
      console.error('Error updating academy profile:', err);
      toast.error('Failed to update academy profile');
      setError('Failed to update academy profile');
    } finally {
      setLoading(false);
    }
  };

  const handleBannerUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !academyId) return;
    setUploadingBanner(true);
    try {
      const body = new FormData();
      body.append('banner', file);
      const response = await fetch(`/api/academies/${academyId}/banner`, {
        method: 'POST',
        body,
      });
      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.message || 'Failed to upload banner');
      }
      toast.success('School / academy photo updated');
      onUpdate();
    } catch (err: any) {
      toast.error(err?.message || 'Failed to upload banner');
    } finally {
      setUploadingBanner(false);
    }
  };

  if (!academyInfo) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Academy Profile
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      <Card>
        <CardContent>
          <Grid container spacing={3}>
            {academyInfo?.academy_id_code && (
              <Grid item xs={12}>
                <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1, mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Academy ID
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#1976d2' }}>
                    {academyInfo.academy_id_code}
                  </Typography>
                  {publicProfileHref ? (
                    <Button
                      size="small"
                      startIcon={<OpenInNewIcon />}
                      href={publicProfileHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ mt: 1, textTransform: 'none' }}
                    >
                      View public profile
                    </Button>
                  ) : null}
                </Box>
              </Grid>
            )}

            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                School / academy photo (public banner)
              </Typography>
              {academyInfo.banner_url ? (
                <Box
                  component="img"
                  src={academyInfo.banner_url}
                  alt="Academy banner"
                  sx={{
                    width: '100%',
                    maxHeight: 180,
                    objectFit: 'cover',
                    borderRadius: 1.5,
                    mb: 1.25,
                    border: '1px solid #e2e8f0',
                  }}
                />
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Upload a wide photo. It appears at the top of the public profile.
                </Typography>
              )}
              <Button
                variant="outlined"
                component="label"
                startIcon={<PhotoCameraIcon />}
                disabled={uploadingBanner}
                sx={{ textTransform: 'none' }}
              >
                {uploadingBanner ? 'Uploading...' : academyInfo.banner_url ? 'Replace photo' : 'Upload photo'}
                <input type="file" accept="image/*" hidden onChange={handleBannerUpload} />
              </Button>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Academy Name *"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </Grid>

            <Grid item xs={12}>
              <PublicProfileUrlField
                value={formData.public_slug}
                onChange={(public_slug) => setFormData({ ...formData, public_slug })}
                exceptType="academy"
                exceptId={academyId}
                suggestedFrom={[formData.name]}
                fallbackCode={academyInfo.academy_id_code}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Academy Type *</InputLabel>
                <Select
                  value={formData.academy_type}
                  label="Academy Type *"
                  onChange={(e) => setFormData({ ...formData, academy_type: e.target.value })}
                >
                  <MenuItem value="regular_school">Regular School</MenuItem>
                  <MenuItem value="college">College</MenuItem>
                  <MenuItem value="sports_academy">Sports Academy</MenuItem>
                  <MenuItem value="corporate">Corporate</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Contact Email"
                type="email"
                value={formData.contact_email}
                onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Contact Phone"
                value={formData.contact_phone}
                onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Country"
                value={formData.country}
                onChange={(e) => setFormData({ ...formData, country: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="City"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                multiline
                rows={2}
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={4}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </Grid>

            <Grid item xs={12}>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={loading}
                sx={{ minWidth: 120 }}
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default AcademyProfile;

